import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, Tab, Alert, Button, Stack, Typography } from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import { Container } from 'Components/Container';
import { PageLoaderShow } from 'Components/PageLoader';
import { useAuthTenant } from 'store/auth';
import { getTransparencyConfig } from 'services/transparency-config-service';
import { GeneralSettings } from './GeneralSettings';
import { FrameworkVisibility } from './FrameworkVisibility';
import { PolicyVisibility } from './PolicyVisibility';
import { AccessControl } from './AccessControl';
import { TokenManagement } from './TokenManagement';
import { AuditLog } from './AuditLog';

/**
 * Transparency Center Configuration
 * - Admin only (transparency_config_w permission)
 * - Tab-based layout for different config sections
 */
export const TransparencyConfig = () => {
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const { t } = useTranslation(['transparency', 'common']);
  const [activeTab, setActiveTab] = useState(0);

  const { isLoading, error, data: config, refetch } = useQuery(
    ['transparencyConfig', selectedTenant?._id],
    () => getTransparencyConfig(selectedTenant?.slug),
    { enabled: !!selectedTenant?.slug }
  );

  if (isLoading) return <PageLoaderShow />;

  if (error) {
    return (
      <Container title={t('transparency:config.errorTitle')}>
        <Alert severity="error">{t('transparency:config.errorMsg')}</Alert>
      </Container>
    );
  }

  return (
    <Container
      title={t('transparency:config.title')}
      breadcrumb={t('transparency:config.breadcrumb')}
    >
      {/* Header with Preview Button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">{t('transparency:config.heading')}</Typography>
        <Button
          variant="outlined"
          startIcon={<PreviewIcon />}
          onClick={() => navigate('/transparency-center/preview')}
        >
          Preview
        </Button>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label={t('transparency:config.tabs.general')} />
          <Tab label={t('transparency:config.tabs.frameworks')} />
          <Tab label={t('transparency:config.tabs.policies')} />
          <Tab label={t('transparency:config.tabs.accessControl')} />
          <Tab label={t('transparency:config.tabs.tokens')} />
          <Tab label={t('transparency:config.tabs.audit')} />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <GeneralSettings config={config.data} onUpdate={refetch} companyKey={selectedTenant?.slug} />
      )}
      {activeTab === 1 && (
        <FrameworkVisibility config={config.data} onUpdate={refetch} companyKey={selectedTenant?.slug} companyId={selectedTenant?._id} />
      )}
      {activeTab === 2 && (
        <PolicyVisibility config={config.data} onUpdate={refetch} companyKey={selectedTenant?.slug} />
      )}
      {activeTab === 3 && (
        <AccessControl config={config.data} onUpdate={refetch} companyKey={selectedTenant?.slug} />
      )}
      {activeTab === 4 && (
        <TokenManagement config={config.data} companyKey={selectedTenant?.slug} />
      )}
      {activeTab === 5 && (
        <AuditLog companyKey={selectedTenant?.slug} />
      )}
    </Container>
  );
};
